// src/Components/Header.js
import React, { useEffect, useRef, useState, useCallback } from "react";
import { Link, useLocation } from "react-router-dom";
import styles from "../styles/Header.module.css";
import { useAuth } from "../auth/AuthContext";

export default function Header() {
  const [showMe, setShowMe] = useState(false);
  const [acctOpen, setAcctOpen] = useState(false);

  const location = useLocation();

  // ✅ IMPORTANT: use SAME logout logic as dashboard
  const { user, logout: authLogout, refreshMe } = useAuth();

  const isHomePage = location.pathname === "/";

  const avatarSrc = (() => {
    try {
      return localStorage.getItem("cz_avatar") || "";
    } catch {
      return "";
    }
  })();

  const acctRef = useRef(null);

  function toggle() {
    setShowMe((s) => !s);
  }

  function closeMenu() {
    setShowMe(false);
    setAcctOpen(false);
  }

  function toggleAccountMenu(e) {
    e.preventDefault();
    e.stopPropagation();
    setAcctOpen((s) => !s);
  }

  /**
   * ✅ Shared logout flow (same behavior as Dashboard.js)
   * - calls AuthContext logout (which calls POST auth/logout and clears local user)
   * - then refreshMe (optional)
   * - then hard redirect to / to fully reset app state
   */
  const doLogout = useCallback(async () => {
    // close flyouts first (better UX)
    closeMenu();

    try {
      // ✅ this should call the same API route your dashboard uses
      await authLogout?.();
    } catch {
      // ignore
    } finally {
      try {
        await refreshMe?.();
      } catch {}

      // clear avatar cache if you want (safe)
      try {
        localStorage.removeItem("cz_avatar");
      } catch {}

      // ✅ hard reset (same as dashboard)
      window.location.assign("/?logout=1");
    }
  }, [authLogout, refreshMe]);

  // close account dropdown on outside click
  useEffect(() => {
    function onDocDown(e) {
      if (!acctRef.current) return;
      if (!acctRef.current.contains(e.target)) setAcctOpen(false);
    }
    document.addEventListener("mousedown", onDocDown);
    return () => document.removeEventListener("mousedown", onDocDown);
  }, []);

  const headerClass = `${styles.header} ${isHomePage ? styles.headerHome : ""}`;

  return (
    <header className={headerClass}>
      {/* Left Menu */}
      <nav className={styles.menuLeft}>
        <ul>
          <li>
            <Link to="/" title="Chinese Zodiac Finder" onClick={closeMenu}>
              Chinese Zodiac Finder
            </Link>
          </li>
          <li>
            <Link to="/chinese-horoscope" title="Chinese Horoscope" onClick={closeMenu}>
              Chinese Horoscope
            </Link>
          </li>
          <li>
            <Link to="/chinese-zodiac" title="The Twelve Zodiac" onClick={closeMenu}>
              The Twelve Zodiac
            </Link>
          </li>
     
        </ul>
      </nav>

      {/* Logo in the Center */}
      <div className={styles.logo}>
        <Link to="/" onClick={closeMenu}>
          <img
            src="/my-chinese-horoscope-logo.png"
            alt="My Chinese Horoscope Finder"
            title="My Chinese Horoscope Finder"
            width={582}
            height={226}
            style={{ display: "block" }}
          />
        </Link>
      </div>

      {/* Right Menu */}
      <nav className={styles.menuRight}>
        <ul>
          <li>
            <Link to="/directory" title="Chinese Horoscope Community Directory" onClick={closeMenu}>
              Resource Directory
            </Link>
          </li>

          <li>
            <Link to="/subscribe" title="Monthly subscription" onClick={closeMenu}>
              Subscribe
            </Link>
          </li>

          {user ? (
            <li ref={acctRef} style={{ position: "relative" }}>
              {/* ✅ My Account button */}
              <a
                href="/dashboard"
                onClick={toggleAccountMenu}
                className={styles.accountLink}
                title="My Account"
              >
                <span className={styles.avatar} aria-hidden="true">
                  {avatarSrc ? (
                    <img src={avatarSrc} alt="" />
                  ) : (
                    <span className={styles.avatarInitial}>
                      {(user?.first_name?.[0] || user?.email?.[0] || "U").toUpperCase()}
                    </span>
                  )}
                </span>
                <span>My Account</span>
              </a>

              {/* ✅ Flyout (Dashboard + Logout) */}
              {acctOpen && (
                <div
                  style={{
                    position: "absolute",
                    right: 0,
                    top: "calc(100% + 8px)",
                    background: "#ffffff",
                    border: "1px solid rgba(0,0,0,0.12)",
                    borderRadius: 12,
                    padding: 10,
                    minWidth: 200,
                    boxShadow: "0 12px 30px rgba(0,0,0,0.18)",
                    zIndex: 99999,
                  }}
                >
                  <Link
                    to="/dashboard"
                    onClick={closeMenu}
                    style={{
                      display: "block",
                      padding: "12px 14px",
                      borderRadius: 10,
                      textDecoration: "none",
                      color: "#0b0b0b",
                      fontWeight: 900,
                      background: "transparent",
                    }}
                  >
                    Dashboard
                  </Link>

                  <div
                    style={{
                      height: 1,
                      background: "rgba(0,0,0,0.08)",
                      margin: "10px 0",
                    }}
                  />

                  {/* ✅ Logout now uses SAME dashboard logout logic */}
                  <button
                    type="button"
                    onClick={doLogout}
                    style={{
                      width: "100%",
                      textAlign: "left",
                      padding: "12px 14px",
                      borderRadius: 10,
                      border: "none",
                      background: "transparent",
                      fontWeight: 900,
                      cursor: "pointer",
                      color: "#0b0b0b",
                    }}
                  >
                    Logout
                  </button>
                </div>
              )}
            </li>
          ) : (
            <li>
              <Link to="/login" title="Login" onClick={closeMenu}>
                Login
              </Link>
            </li>
          )}
        </ul>
      </nav>

      {/* Hamburger Menu */}
      <div
        className={styles.hamburger}
        onClick={toggle}
        role="button"
        aria-label="Toggle menu"
        aria-expanded={showMe}
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") toggle();
        }}
      >
        <div></div>
        <div></div>
        <div></div>
      </div>

      {/* Fullscreen (Mobile) Menu */}
      {showMe && (
        <nav className={`${styles.menuFullscreen} ${styles.showMenu}`}>
          <div
            className={styles.closeMenu}
            onClick={closeMenu}
            role="button"
            aria-label="Close menu"
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") closeMenu();
            }}
          >
            &times;
          </div>

          <ul className={styles.MobileMenu}>
            <li>
              <Link to="/" onClick={closeMenu}>
                Find Your Zodiac
              </Link>
            </li>
            <li>
              <Link to="/chinese-horoscope" onClick={closeMenu}>
                Chinese Horoscope
              </Link>
            </li>
            <li>
              <Link to="/chinese-zodiac" onClick={closeMenu}>
                The Twelve Zodiac
              </Link>
            </li>
         
           <li>
              <Link to="/directory" onClick={closeMenu}>
                Community Directory
              </Link>
            </li>
            <li>
              <Link to="/subscribe" onClick={closeMenu}>
                Subscribe
              </Link>
            </li>

            {user ? (
              <>
                <li>
                  <Link to="/dashboard" onClick={closeMenu}>
                    Dashboard
                  </Link>
                </li>
                <li>
                  <button className={styles.linkBtn} onClick={doLogout} type="button">
                    Logout
                  </button>
                </li>
              </>
            ) : (
              <li>
                <Link to="/login" onClick={closeMenu}>
                  Login
                </Link>
              </li>
            )}
          </ul>
        </nav>
      )}
    </header>
  );
}